import { Activity, ChecklistItem, Trip } from '../../../services/trips/api/tripsApi';
import { formatMoney, formatSpend } from '../../../services/trips/utils/money';
import { formatBudget } from '../../../services/trips/utils/tripUtils';
import { formatTripDates } from '../../../services/trips/utils/dateUtils';

/**
 * The trip as a printable document.
 *
 * A pure string builder: trip + stops + roster + checklist in, HTML out. The
 * markup and the stylesheet are deliberately identical to the mobile app's
 * `src/lib/trip-pdf.ts`, so the two platforms produce the same document from
 * the same trip. What differs is only the field names either side reads —
 * the web's Activity is the flat UI view (`dateTime`, `location` as a string,
 * `budget`) where the phone holds the server shape (`startTime`, a location
 * object, `cost`). The adapter between them lives in tripsApi.
 *
 * NO MAP. A static map image needs the Maps Static API, and that key is
 * server-side only now (SEC-1) — a map would mean a new server endpoint. A
 * written itinerary with travel times stands on its own.
 */

export interface PdfTraveller {
  name: string;
  role: string;
}

export interface TripPdfInput {
  trip: Trip;
  activities: Activity[];
  travellers: PdfTraveller[];
  checklist: ChecklistItem[];
}

/**
 * Everything interpolated below comes from the trip: titles a collaborator
 * typed, place names the agent suggested, checklist lines. None of it is ours,
 * and the result is written into an iframe document — so it is escaped on the
 * way in rather than trusted.
 */
function escape(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** YYYY-MM-DD in the reader's own calendar, so a stop lands on the day it reads. */
function localDateKey(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function dayKeyOf(activity: Activity): string {
  if (!activity.dateTime) return '';
  const date = new Date(activity.dateTime);
  return Number.isNaN(date.getTime()) ? '' : localDateKey(date);
}

/** "09:00", or nothing when the stop has no time. */
function timeOf(activity: Activity): string {
  if (!activity.dateTime) return '';
  const date = new Date(activity.dateTime);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/** "Saturday 17 October" — the heading a day is found by. */
function dayHeading(key: string): string {
  const [year, month, day] = key.split('-').map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1);
  if (Number.isNaN(date.getTime())) return key;
  return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

function byDay(activities: Activity[]): { key: string; stops: Activity[] }[] {
  const days = new Map<string, Activity[]>();
  for (const activity of activities) {
    const key = dayKeyOf(activity);
    const stops = days.get(key);
    if (stops) stops.push(activity);
    else days.set(key, [activity]);
  }

  return [...days.entries()]
    .map(([key, stops]) => ({
      key,
      stops: [...stops].sort((a, b) => (a.dateTime || '').localeCompare(b.dateTime || '')),
    }))
    // Dated days in order; the undated group ('') last, under its own heading.
    .sort((a, b) => (a.key === '' ? 1 : b.key === '' ? -1 : a.key.localeCompare(b.key)));
}

/** "2h 15m", "45m", or nothing when there is nothing to add up. */
function duration(minutes: number): string {
  if (minutes <= 0) return '';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}m`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

function dayTotals(stops: Activity[]): string {
  const visiting = stops.reduce((sum, stop) => sum + (stop.visitMinutes || 0), 0);
  /**
   * From the SECOND stop on — the same legs the day prints. The first stop's
   * leg comes from wherever the previous day ended, so the day does not draw
   * it; counting it anyway produces a total nothing on the page accounts for.
   */
  const travelling = stops
    .slice(1)
    .reduce((sum, stop) => sum + (stop.travelFromPrev?.minutes || 0), 0);
  // One subtotal per currency. Added together and labelled "$" — which is what
  // this did — a euro lunch and a dollar museum printed a total that is true in
  // neither, in a document the tester keeps and forwards.
  const spend = formatSpend(stops.map((stop) => stop.budget));

  return [
    `${stops.length} stop${stops.length === 1 ? '' : 's'}`,
    visiting ? `${duration(visiting)} visiting` : '',
    travelling ? `${duration(travelling)} travelling` : '',
    spend,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * The hop into a stop, in words — no emoji. Colour emoji print inconsistently
 * and carry a tone a printed itinerary does not want.
 *
 * A leg can EXIST with `minutes: null` — the stored shape when Google could not
 * route between two stops. Printing nothing then reads as "this trip has no
 * travel times at all"; name the hop that is unknown instead.
 */
function legLine(leg?: Activity['travelFromPrev']): string {
  if (!leg) return '';
  const mode = leg.mode === 'walk' ? 'Walk' : leg.mode === 'transit' ? 'Transit' : 'Drive';
  if (!leg.minutes && !leg.km) return `${mode} · travel time unknown`;

  const minutes = leg.minutes ?? 0;
  const time =
    minutes >= 60 ? `${Math.floor(minutes / 60)} h ${minutes % 60} min` : `${minutes} min`;
  const km = leg.km ?? 0;
  const distance = km ? ` · ${km < 10 ? km.toFixed(1) : Math.round(km)} km` : '';
  return `${mode} · ≈ ${time}${distance}`;
}

/**
 * A Google Maps link for a stop, the same way the place card builds one.
 *
 * The card can use `mapsUri` because it has already fetched the place; here
 * there is only what the itinerary stores, and fetching details for every stop
 * would turn one document into N network calls. The documented `?api=1&query=`
 * form needs none of that — and with `query_place_id` it resolves to the EXACT
 * place rather than to whatever a text search for "Two Sizes" finds in Rome.
 *
 * Printed on paper a link is invisible, which is fine: this document is read on
 * a phone as often as it is printed, and nothing about the page changes for the
 * reader who never clicks it.
 */
function mapsUrl(activity: Activity): string {
  const text = activity.location || activity.title;
  if (!text) return '';

  const query = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`;
  return activity.placeId
    ? `${query}&query_place_id=${encodeURIComponent(activity.placeId)}`
    : query;
}

function stopRow(activity: Activity, index: number): string {
  const cost = formatMoney(activity.budget);
  const url = mapsUrl(activity);
  const leg = index > 0 ? legLine(activity.travelFromPrev) : '';

  return `
    ${leg ? `<div class="leg">↓ ${escape(leg)}</div>` : ''}
    <div class="stop">
      <div class="stop-time">${escape(timeOf(activity))}</div>
      <div class="stop-body">
        <div class="stop-title">${escape(activity.title)}</div>
        ${
          activity.location
            ? `<div class="stop-where">${
                url ? `<a href="${escape(url)}">${escape(activity.location)}</a>` : escape(activity.location)
              }</div>`
            : ''
        }
      </div>
      <div class="stop-cost">${escape(cost || 'Free')}</div>
    </div>`;
}

function daySection(key: string, stops: Activity[]): string {
  return `
    <section class="day">
      <h3>${key ? escape(dayHeading(key)) : 'Not scheduled'}</h3>
      <div class="day-totals">${escape(dayTotals(stops))}</div>
      ${stops.map(stopRow).join('')}
    </section>`;
}

/** Nights between the two dates, inclusive of both days. */
function dayCount(trip: Trip): number {
  const start = trip.dates?.startDate ? new Date(`${trip.dates.startDate}T00:00:00`) : null;
  const end = trip.dates?.endDate ? new Date(`${trip.dates.endDate}T00:00:00`) : null;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
}

export function buildTripPdfHtml({
  trip,
  activities,
  travellers,
  checklist,
}: TripPdfInput): string {
  const days = byDay(activities);
  const count = dayCount(trip);
  const perDay = (() => {
    const budget = typeof trip.budget === 'object' && trip.budget ? trip.budget : null;
    if (!budget?.amount || count <= 0) return '';
    // Through formatBudget so the symbol matches the total beside it rather
    // than defaulting to a dollar for a trip budgeted in euros. It now rounds
    // to whole units itself — this used to print what it was given, so 2000/9
    // came out as "C$222.222/day" here while the phone showed "C$222".
    return `${formatBudget({ amount: budget.amount / count, currency: budget.currency })}/day`;
  })();

  const travellerCount = trip.travelers
    ? Object.values(trip.travelers).reduce<number>(
        (sum, value) => sum + (typeof value === 'number' ? value : 0),
        0,
      )
    : 0;

  const facts = [
    formatTripDates(trip.dates, { style: 'compact' }),
    count ? `${count} days` : '',
    travellerCount ? `${travellerCount} traveller${travellerCount === 1 ? '' : 's'}` : '',
    `${activities.length} stops`,
    formatBudget(trip.budget),
    perDay,
  ].filter(Boolean);

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escape(trip.title)}</title>
<style>
  /* Margins that are safe on A4 and Letter alike — the narrower of the two. */
  @page { margin: 16mm 14mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
    font-size: 11pt;
    line-height: 1.45;
    color: #1d2a24;
  }
  h1 { font-size: 20pt; margin: 0 0 4px; color: #0c342c; }
  h2 {
    font-size: 11pt;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #5d6b62;
    margin: 22px 0 8px;
    border-bottom: 1px solid #e2e6d8;
    padding-bottom: 4px;
  }
  h3 { font-size: 12pt; margin: 0 0 2px; color: #0c342c; }
  .facts { color: #5d6b62; font-size: 10pt; }
  .about { margin-top: 6px; }
  .people { margin: 0; padding: 0; list-style: none; }
  .people li { padding: 3px 0; }
  .role { color: #5d6b62; font-size: 9.5pt; }

  /*
    A stop is never split across a page, and a day keeps its heading with at
    least its first stop — a page that opens with an orphaned time and no date
    is unreadable on paper, where you cannot scroll back.
  */
  .day { margin-top: 14px; break-inside: auto; }
  .day h3 { break-after: avoid; }
  .day-totals { color: #5d6b62; font-size: 9.5pt; margin-bottom: 8px; break-after: avoid; }
  .stop {
    display: flex;
    gap: 10px;
    align-items: baseline;
    padding: 6px 0;
    border-bottom: 1px solid #eef0e6;
    break-inside: avoid;
  }
  .stop-time { width: 52px; flex: none; font-weight: 700; color: #0c342c; }
  .stop-body { flex: 1; }
  .stop-title { font-weight: 600; }
  .stop-where { color: #5d6b62; font-size: 10pt; }
  /*
    Inherits its colour and marks itself with a dotted rule rather than going
    link-blue and underlined: on paper that styling is decoration for something
    nobody can click, and on screen this still reads as a link.
  */
  .stop-where a {
    color: inherit;
    text-decoration: underline dotted;
    text-underline-offset: 2px;
  }
  .stop-cost { flex: none; color: #5d6b62; font-size: 10pt; }
  .leg { color: #5d6b62; font-size: 9.5pt; padding: 4px 0 4px 62px; break-inside: avoid; }

  .list { margin: 0; padding: 0; list-style: none; }
  .list li { padding: 4px 0; break-inside: avoid; }
  .box { color: #0c342c; margin-right: 6px; }
  .note { color: #5d6b62; font-size: 10pt; }
  footer { margin-top: 26px; color: #9aa79d; font-size: 9pt; }
</style>
</head>
<body>
  <h1>${escape(trip.title)}</h1>
  <div class="facts">${escape(facts.join(' · '))}</div>
  ${
    trip.destinations?.length
      ? `<div class="facts">${escape(trip.destinations.join(' · '))}</div>`
      : ''
  }

  ${
    trip.description
      ? `<h2>About this trip</h2><div class="about">${escape(trip.description)}</div>`
      : ''
  }

  ${
    travellers.length
      ? `<h2>Who's going</h2>
         <ul class="people">
           ${travellers
             .map(
               (person) =>
                 `<li>${escape(person.name)} <span class="role">· ${escape(person.role)}</span></li>`,
             )
             .join('')}
         </ul>`
      : ''
  }

  ${
    days.length
      ? `<h2>Day by day</h2>${days.map((day) => daySection(day.key, day.stops)).join('')}`
      : ''
  }

  ${
    checklist.length
      ? `<h2>Things to take</h2>
         <ul class="list">
           ${checklist
             .map(
               (item) =>
                 `<li><span class="box">${item.checked ? '☑' : '☐'}</span>${escape(item.title)}${
                   item.description ? ` <span class="note">— ${escape(item.description)}</span>` : ''
                 }</li>`,
             )
             .join('')}
         </ul>`
      : ''
  }

  <footer>Wayplume</footer>
</body>
</html>`;
}
